import { date } from '@seek/ca-myprofile-utils';
import { useTranslations } from '@vocab/react';
import {
  Text,
  ButtonIcon,
  Badge,
  Inline,
  Stack,
  Box,
  IconResume,
  OverflowMenu,
  MenuItem,
  Heading,
  Columns,
  Column,
  IconDownload,
  IconDelete,
  IconTick,
  Card,
} from 'braid-design-system';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { RESUME } from 'src/components/Router';
import { useAnalytics } from 'src/modules/analytics/AnalyticsProvider';
import { useAppConfig } from 'src/modules/appConfig';
import { useTypedSelector } from 'src/modules/hooks/useTypedSelector';
import { stateForId } from 'src/modules/syncing';
import { VirusScanStatus } from 'src/types/graphql';

import { getResumeLink, requestDeleteResume } from '../../../actionCreators';
import { useMakeDefaultResume } from '../../../data';
import type { Resume } from '../../../types';

import translations from './.vocab';
import { ConfirmDelete } from './ConfirmDelete/ConfirmDelete';
import { ErrorMessage } from './ErrorMessage/ErrorMessage';
import { ErrorMessageType, getErrorMessageType } from './ErrorMessage/utils';
import { resumeDefaultAlertLoaded } from './actionCreators';
import { useSensitivePrivacyDialog } from './useSensitivePrivacyDialog';

import * as styles from './ResumeDetailItem.css';

const ANIMATION_DURATION = 350;

interface ResumeDetailItemProps extends Resume {
  highlightResume: boolean;
  readOnly?: boolean;
  shouldRunAnalytics?: boolean;
  loading?: boolean;
}

export const ResumeDetailItem = ({
  id,
  createdDateUtc,
  fileMetadata: { name, virusScanStatus, sensitiveDataInfo },
  highlightResume,
  deleteRequested = false,
  fetchedStatus,
  origin,
  readOnly = false,
  shouldRunAnalytics = true,
  loading = false,
  isDefault,
}: ResumeDetailItemProps) => {
  const { t } = useTranslations(translations);
  const [makeDefaultResume] = useMakeDefaultResume();
  const { language } = useAppConfig();
  const { dialog, setShowDialog } = useSensitivePrivacyDialog();
  const { action, state: resumeSyncState } = useTypedSelector(
    (store) => {
      const { syncing } = store;
      return stateForId({
        state: { syncing },
        parent: RESUME,
        id,
      });
    },
    (prevState, nextState) =>
      JSON.stringify(prevState) === JSON.stringify(nextState),
  );
  const hasSensitiveInfo = sensitiveDataInfo?.isDetected || false;
  const hasVirus = virusScanStatus === VirusScanStatus.HasVirus;
  const isPending =
    typeof fetchedStatus !== 'undefined' &&
    fetchedStatus === true &&
    virusScanStatus === VirusScanStatus.Pending;
  const errorType = getErrorMessageType({
    action,
    resumeSyncState,
    hasSensitiveInfo,
    hasVirus,
    isPending,
  });
  const [dateAdded, setDateAdded] = useState('');
  const dispatch = useDispatch();

  const updateAddedDate = useCallback(
    () =>
      date.formatDateTime(createdDateUtc, language).then((dateString) => {
        setDateAdded(dateString);
      }),
    [createdDateUtc, language],
  );

  useEffect(() => {
    updateAddedDate();
    const interval = setInterval(() => {
      updateAddedDate();
    }, 60 * 1000);

    return () => {
      clearInterval(interval);
    };
  }, [updateAddedDate]);

  const [showAnimation, setShowAnimation] = useState(false);

  const { resumeDeletePressed, resumeSensitiveInfoDialogDisplayed } =
    useAnalytics();

  useEffect(() => {
    if (!loading && shouldRunAnalytics) {
      switch (errorType) {
        case ErrorMessageType.SensitiveInformation:
          dispatch(
            resumeDefaultAlertLoaded({
              alertShownType: 'notice',
              alertShownTone: 'caution',
              alertShownPosition: 'content',
              alertShownTitle: 'resume with sensitive information',
              alertShownMessage: 'May contain sensitive information.',
            }),
          );

          break;
        case ErrorMessageType.Virus:
          dispatch(
            resumeDefaultAlertLoaded({
              alertShownType: 'notice',
              alertShownTone: 'critical',
              alertShownPosition: 'content',
              alertShownTitle: 'resume with virus detected',
              alertShownMessage: 'Virus detected. Resume cant be used.',
            }),
          );

          break;
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let startTimer: NodeJS.Timeout;
    let animationTimer: NodeJS.Timeout;

    if (highlightResume) {
      const ANIMATION_START_DELAY =
        origin && origin.type === 'Generated' ? 1800 : 0;

      startTimer = setTimeout(() => {
        setShowAnimation(true);
        clearTimeout(startTimer);

        animationTimer = setTimeout(() => {
          setShowAnimation(false);
        }, ANIMATION_DURATION);
      }, ANIMATION_START_DELAY);
    }

    return () => {
      if (typeof startTimer !== 'undefined') {
        clearTimeout(startTimer);
      }

      if (typeof animationTimer !== 'undefined') {
        clearTimeout(animationTimer);
      }
    };
  }, [id, origin, dispatch, highlightResume]);

  const originValue = origin?.type ?? '';

  const showCreatedFromProfileBadge = originValue === 'Generated';

  const rootClasses = {
    [styles.root]: true,
    [styles.rootAnimate]: showAnimation,
  };

  const hasBadges = isDefault || showCreatedFromProfileBadge || highlightResume;

  const deleteOnClick = useCallback(() => {
    resumeDeletePressed({
      eventName: 'resume_delete_pressed',
      isResumeDefault: isDefault,
      updateType: 'delete',
      resumeType: origin.type === 'Generated' ? 'generated' : 'uploaded',
    });

    dispatch(requestDeleteResume(id));
  }, [dispatch, id, isDefault, origin.type, resumeDeletePressed]);

  const onSensitiveInfoClick = useCallback(() => {
    setShowDialog(true);
    resumeSensitiveInfoDialogDisplayed({
      eventName: 'sensitive_information_dialog_displayed',
      isResumeDefault: isDefault,
      resumeType: origin.type === 'Generated' ? 'generated' : 'uploaded',
    });
  }, [
    isDefault,
    origin.type,
    resumeSensitiveInfoDialogDisplayed,
    setShowDialog,
  ]);

  const downloadOnClick = () => {
    dispatch(getResumeLink(id, originValue));
  };

  const ResumeDetail = () => (
    <Box className={rootClasses} data-automation={`resume-item-${id}`}>
      <Columns
        space="small"
        alignY={hasBadges || readOnly ? 'center' : undefined}
      >
        {!readOnly ? (
          <Column width="content">
            <Heading level="4" component="span">
              <IconResume tone="secondary" />
            </Heading>
          </Column>
        ) : null}
        <Column>
          <Stack space="medium">
            {hasBadges && (
              <Inline space="small">
                {highlightResume ? (
                  <Badge tone="positive" bleedY>
                    {t('New')}
                  </Badge>
                ) : null}
                {isDefault && (
                  <Badge
                    tone="neutral"
                    bleedY
                    data={{ automation: `resume-is-default-${id}` }}
                  >
                    {t('Default')}
                  </Badge>
                )}
                {showCreatedFromProfileBadge && (
                  <Badge bleedY tone="info">
                    {t('Created from profile')}
                  </Badge>
                )}
              </Inline>
            )}
            <Heading level="4" component="span">
              <span className={styles.resumeName}>{name}</span>
            </Heading>
            <Stack space="small">
              <Text size="small" tone="secondary">
                {dateAdded}
              </Text>
              {errorType ? (
                <ErrorMessage
                  id={id}
                  errorType={errorType}
                  onSensitiveInfoClick={onSensitiveInfoClick}
                  origin={originValue}
                  onDelete={deleteOnClick}
                />
              ) : null}
            </Stack>
          </Stack>
        </Column>
        <Column width="content">
          {!readOnly ? (
            <OverflowMenu label={`Options for ${name}`}>
              {!hasVirus && !isDefault && (
                <MenuItem
                  onClick={() =>
                    makeDefaultResume(
                      { resumeId: { id }, languageCode: language },
                      { origin: originValue },
                    )
                  }
                  data={{ automation: `resume-make-default-${id}` }}
                  icon={<IconTick />}
                >
                  {t('Make default')}
                </MenuItem>
              )}
              {!hasVirus && (
                <MenuItem
                  data-hj-masked
                  data={{
                    automation: `resume-download-link-menu-${id}`,
                  }}
                  onClick={downloadOnClick}
                  icon={<IconDownload />}
                >
                  {t('Download')}
                </MenuItem>
              )}
              <MenuItem
                tone="critical"
                onClick={deleteOnClick}
                data={{ automation: `delete-resume-button-${id}` }}
                icon={<IconDelete />}
              >
                {t('Delete')}
              </MenuItem>
            </OverflowMenu>
          ) : (
            <ButtonIcon
              id={id}
              onClick={downloadOnClick}
              data={{ automation: `resume-download-link-${id}` }}
              variant="transparent"
              icon={<IconDownload />}
              label={t('Download')}
            />
          )}
        </Column>
      </Columns>

      <ConfirmDelete id={id} origin={originValue} open={deleteRequested} />
    </Box>
  );

  return (
    <>
      {readOnly ? (
        <ResumeDetail />
      ) : (
        <Card>
          <ResumeDetail />
        </Card>
      )}
      {dialog}
    </>
  );
};
